import { useEffect, useState } from "react";
import { Type, LayoutTemplate, FileUp, FileDown, FileSignature, FileArchive, Images, Zap, Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { BrandMark } from "../Logo";

const KEY = "facio_tour_seen_v1";

const STEPS = [
  { Icon: Type, title: "Create from scratch", body: "Use the Rich Text editor for free-form documents, or fill in a polished Template (invoice, resume, report and more). These are Basic — 1 credit." },
  { Icon: FileUp, title: "Convert files", body: "Drop Word, Excel, PowerPoint or images to turn them into a PDF — or go the other way with PDF → Word / Images. Standard tier, and large files add a little for every 30 pages." },
  { Icon: FileSignature, title: "Creator Tools", body: "Built for solopreneurs: ready-to-sign Agreements, a PDF Compressor to shrink big files, and a Carousel exporter that slices decks into social-ready images." },
  { Icon: Zap, title: "You always see the cost first", body: "Every tool shows its exact credit cost before you export — and file tools let you preview and Approve & Download. No surprises." },
];

export default function WelcomeTour() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("intro"); // intro | tour
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setOpen(true);
  }, []);

  const finish = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const s = STEPS[step];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent className="max-w-md" data-testid="welcome-tour">
        <DialogTitle className="sr-only">Welcome to Facio Doc</DialogTitle>
        <DialogDescription className="sr-only">A quick optional tour of the Facio Doc studio tools and pricing.</DialogDescription>
        {mode === "intro" ? (
          <div className="text-center py-2">
            <div className="flex justify-center mb-4"><BrandMark className="h-14 w-14" textClass="text-2xl" /></div>
            <h2 className="font-display text-2xl font-bold">Welcome to Facio Doc</h2>
            <p className="text-slate-600 mt-2 text-sm">Your pay-as-you-go studio for creating, converting and polishing PDFs. Want a 30-second look around?</p>
            <div className="mt-6 flex flex-col gap-2">
              <Button data-testid="tour-start" onClick={() => setMode("tour")} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-11">
                <Sparkles className="h-4 w-4 mr-2" /> Take the quick tour
              </Button>
              <button data-testid="tour-skip" onClick={finish} className="text-sm text-slate-500 hover:text-slate-700 py-1">
                I'll explore myself
              </button>
            </div>
          </div>
        ) : (
          <div className="py-2">
            <div className="flex items-center gap-1.5 mb-4">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full flex-1 transition-colors ${i <= step ? "bg-indigo-600" : "bg-slate-200"}`} />
              ))}
            </div>
            <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4"><s.Icon className="h-6 w-6 text-indigo-600" /></div>
            <h2 className="font-display text-xl font-bold">{s.title}</h2>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">{s.body}</p>
            <div className="mt-6 flex items-center justify-between">
              <button
                data-testid="tour-back"
                onClick={() => setStep((x) => Math.max(0, x - 1))}
                disabled={step === 0}
                className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-0 flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <Button data-testid="tour-next" onClick={() => setStep((x) => x + 1)} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6">
                  Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button data-testid="tour-done" onClick={finish} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6">
                  Start creating <Check className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
